/**
 * HidroVerse Prospect Onboarding form schema.
 *
 * Built from the Nuno PDF (prospect info sheet), expanded with
 * recommendations from the team:
 *   • Power & Utilities (single-/three-phase, generator, load-shedding)
 *   • Known Issues & Pain Points (free-form + recurring complaints)
 *   • Health, Safety & Compliance (SANS 241, effluent, environmental)
 *   • Site Photos & Documents (upload section)
 *   • Sales Rep Sign-off (signature pad + recommended next steps)
 *
 * Field types:
 *   text | textarea | number | date | select | multiselect | radio | bool | gps
 *
 * `pre_fill` is a function (lead, customer, site) → value used for
 * read-only auto-filled fields. Uses 3-way prefill cascade:
 *   1. Customer record (rich profile data, source of truth)
 *   2. Lead record (sales-stage data, may have updates)
 *   3. Site record (location-specific data)
 * Always uses nullish-coalescing so explicit user inputs are preserved.
 */

const yesNo = ["Yes", "No"];
const yesNoPending = ["Yes", "No", "Pending"];

// Map customer.industry (lowercase enum) → onboarding property_type select option
const _INDUSTRY_TO_PROPERTY_TYPE = {
  lodge: "Lodge",
  hotel: "Hotel chain",
  restaurant: "Restaurant",
  agriculture: "Agricultural",
  food_beverage: "Industrial",
  industrial: "Industrial",
  manufacturing: "Industrial",
  mining: "Industrial",
  retail: "Other",
  office: "Other",
  residential: "Residential",
  individual: "Residential",
  other: "Other",
};
const mapIndustryToPropertyType = (ind) => {
  if (!ind) return "";
  return _INDUSTRY_TO_PROPERTY_TYPE[String(ind).toLowerCase()] || "";
};

// Star rating: customer stores int (1-5), schema is select on string
const starRatingStr = (n) => {
  if (n === null || n === undefined || n === "") return "";
  const v = Number(n);
  if (!Number.isFinite(v)) return "";
  if (v < 1) return "Unrated";
  return String(Math.min(5, Math.max(1, Math.round(v))));
};

// First non-empty value (treats empty string / null / undefined as missing)
const firstOf = (...vals) => {
  for (const v of vals) {
    if (v !== null && v !== undefined && v !== "") return v;
  }
  return "";
};

export const ONBOARDING_SECTIONS = [
  {
    key: "identification",
    title: "1. Identification",
    description: "Pre-filled from the lead — confirm or edit.",
    fields: [
      { key: "client_id", label: "Client ID", type: "text", pre_fill: (l, c) => firstOf(c?.external_ref, l?.external_ref) },
      { key: "business_name", label: "Business name", type: "text", required: true, pre_fill: (l, c) => firstOf(c?.name, l?.customer_name) },
      { key: "trading_as", label: "Trading as", type: "text" },
      {
        key: "property_type",
        label: "Property type",
        type: "select",
        options: ["Lodge", "Guesthouse", "Boutique Hotel", "Tented Camp", "Hotel chain", "Resort", "Restaurant", "Industrial", "Agricultural", "Residential", "Other"],
        pre_fill: (l, c) => mapIndustryToPropertyType(firstOf(c?.industry, l?.industry)),
      },
      { key: "star_rating", label: "Star rating", type: "select", options: ["Unrated", "1", "2", "3", "4", "5"], pre_fill: (l, c) => starRatingStr(firstOf(c?.star_rating, l?.star_rating)) },
    ],
  },
  {
    key: "location",
    title: "2. Location & Logistics",
    fields: [
      { key: "physical_address", label: "Physical address", type: "textarea", pre_fill: (l, c, s) => firstOf(s?.address, c?.physical_address, c?.billing_address) },
      { key: "gps_lat", label: "GPS latitude", type: "text", placeholder: "-25.4", pre_fill: (l, c, s) => firstOf(s?.lat, s?.gps_lat) },
      { key: "gps_long", label: "GPS longitude", type: "text", placeholder: "31.05", pre_fill: (l, c, s) => firstOf(s?.lng, s?.gps_long) },
      { key: "nearest_town", label: "Nearest town", type: "text", pre_fill: (l, c, s) => firstOf(s?.nearest_town, c?.province_area) },
      { key: "in_private_reserve", label: "Private game reserve?", type: "radio", options: yesNo },
      { key: "reserve_name", label: "Reserve name (if applicable)", type: "text", show_if: { in_private_reserve: "Yes" } },
      { key: "access_gates", label: "Gates / boom rules", type: "text" },
      { key: "access_permits", label: "Permits required", type: "text" },
      { key: "needs_4x4", label: "4x4 needed?", type: "radio", options: yesNo },
      { key: "opening_times", label: "Opening times", type: "text" },
      { key: "time_zone", label: "Time zone", type: "select", options: ["SAST (UTC+2)", "EAT (UTC+3)", "CAT (UTC+2)", "Other"] },
      { key: "distance_to_base_km", label: "Distance to HidroVerse base (km)", type: "number" },
    ],
  },
  {
    key: "contacts",
    title: "3. Primary Contacts",
    description: "Owner, on-site manager, and maintenance lead.",
    repeat_groups: [
      {
        prefix: "owner",
        title: "Owner",
        fields: [
          { key: "name", label: "Name", type: "text", required: true, pre_fill: (l, c) => firstOf(c?.contact_name, l?.own_contact_name) },
          { key: "role", label: "Role", type: "text", pre_fill: (l, c) => firstOf(c?.contact_position) },
          { key: "phone", label: "Cell phone", type: "text", pre_fill: (l, c) => firstOf(c?.phone, l?.own_contact_phone, l?.customer_phone) },
          { key: "email", label: "Email", type: "text", pre_fill: (l, c) => firstOf(c?.email, l?.own_contact_email, l?.customer_email) },
        ],
      },
      {
        prefix: "manager",
        title: "Manager",
        fields: [
          { key: "name", label: "Name", type: "text" },
          { key: "role", label: "Role", type: "text" },
          { key: "phone", label: "Cell phone", type: "text" },
          { key: "email", label: "Email", type: "text" },
        ],
      },
      {
        prefix: "maint_manager",
        title: "Maintenance manager",
        fields: [
          { key: "name", label: "Name", type: "text" },
          { key: "role", label: "Role", type: "text" },
          { key: "phone", label: "Cell phone", type: "text" },
          { key: "email", label: "Email", type: "text" },
        ],
      },
    ],
    fields: [
      { key: "reservations_email", label: "Reservations email", type: "text" },
      { key: "reception_phone", label: "Reception phone", type: "text", pre_fill: (l, c) => firstOf(c?.phone, l?.customer_phone) },
      { key: "preferred_contact_method", label: "Preferred contact method", type: "select", options: ["WhatsApp", "Email", "Phone", "SMS", "In person"] },
      { key: "linkedin_profile", label: "LinkedIn profile", type: "text" },
      { key: "contact_notes", label: "Notes (best time to call, gatekeepers, etc.)", type: "textarea" },
    ],
  },
  {
    key: "operations",
    title: "4. Property Operations",
    description: "Helps us size the system correctly.",
    fields: [
      { key: "max_guest_capacity", label: "Max guest capacity", type: "number" },
      { key: "staff_capacity", label: "Staff capacity (on-site head count)", type: "number" },
      { key: "rooms", label: "Rooms / units", type: "number" },
      { key: "occupancy_rate_pct", label: "Average occupancy %", type: "number" },
      { key: "peak_season", label: "Peak season (months)", type: "text" },
      { key: "primary_market", label: "Primary market", type: "select", options: ["Domestic", "International", "Both"] },
      { key: "amenity_pool", label: "Pool", type: "radio", options: yesNo },
      { key: "amenity_spa", label: "Spa", type: "radio", options: yesNo },
      { key: "amenity_kitchen", label: "Commercial kitchen", type: "radio", options: yesNo },
      { key: "amenity_laundry", label: "On-site laundry", type: "radio", options: yesNo },
      { key: "amenity_irrigation", label: "Garden / landscape irrigation", type: "radio", options: yesNo },
      { key: "amenity_other", label: "Other amenities (gym, conference, etc.)", type: "text" },
      { key: "estimated_daily_water_usage", label: "Estimated daily water usage (kL/day)", type: "number" },
    ],
  },
  {
    key: "water_source",
    title: "5. Water Source & Infrastructure",
    fields: [
      { key: "has_borehole", label: "Has borehole?", type: "radio", options: yesNo },
      { key: "borehole_count", label: "Number of boreholes", type: "number", show_if: { has_borehole: "Yes" } },
      { key: "borehole_depth_m", label: "Borehole depth (m)", type: "number", show_if: { has_borehole: "Yes" } },
      { key: "borehole_yield", label: "Borehole yield (L/hr or L/min)", type: "text", show_if: { has_borehole: "Yes" } },
      { key: "borehole_cert_available", label: "Borehole certificate available?", type: "radio", options: yesNo, show_if: { has_borehole: "Yes" } },
      { key: "municipal_supply", label: "Municipal supply connected?", type: "radio", options: yesNo },
      { key: "surface_water_sources", label: "Surface water sources", type: "multiselect", options: ["River", "Stream", "Dam", "Lake", "None"] },
      { key: "rainwater_harvesting", label: "Rainwater harvesting?", type: "radio", options: yesNo },
      { key: "tank_total_volume_l", label: "Total storage tank volume (L)", type: "number" },
      { key: "tank_count", label: "Number of tanks", type: "number" },
      { key: "tank_types", label: "Tank types", type: "multiselect", options: ["Plastic", "Steel", "Concrete", "Bladder", "Fibreglass"] },
      { key: "tank_locations", label: "Tank locations", type: "multiselect", options: ["Roof", "Ground", "Pump room", "Underground"] },
      { key: "pump_room_count", label: "Number of pump rooms", type: "number" },
      { key: "emergency_backup_source", label: "Emergency backup water source", type: "select", options: ["Tanker", "Neighbour", "Municipal", "None"] },
    ],
  },
  {
    key: "power",
    title: "6. Power & Utilities",
    description: "NEW — critical for sizing pumps, RO systems, UV reactors.",
    fields: [
      { key: "power_supply", label: "Power supply", type: "select", options: ["Single phase", "Three phase", "Both available", "Generator only", "Solar + battery", "Hybrid"] },
      { key: "voltage_v", label: "Voltage (V)", type: "select", options: ["220-230 V", "380-400 V", "Other"] },
      { key: "voltage_stability", label: "Voltage stability", type: "select", options: ["Stable", "Frequent dips", "Frequent blackouts", "Severe load-shedding"] },
      { key: "loadshedding_hours_per_day", label: "Load-shedding hours per day (avg)", type: "number" },
      { key: "backup_power", label: "Backup power", type: "multiselect", options: ["Generator", "UPS", "Solar", "Battery storage", "None"] },
      { key: "generator_kva", label: "Generator capacity (kVA)", type: "number" },
      { key: "available_amperage", label: "Available amperage at panel (A)", type: "number" },
      { key: "distance_panel_to_pumproom_m", label: "Distance from panel to pump room (m)", type: "number" },
      { key: "power_notes", label: "Power notes (DB board age, certs, restrictions)", type: "textarea" },
    ],
  },
  {
    key: "pumps",
    title: "7. Pumps & Distribution",
    fields: [
      { key: "pump_types", label: "Pump types on site", type: "multiselect", options: ["Centrifugal", "Submersible", "Jet", "Positive displacement", "Booster", "Pressure pump"] },
      { key: "pump_count", label: "How many pumps?", type: "number" },
      { key: "pump_brands_models", label: "Pump brands / models", type: "textarea" },
      { key: "pump_capacity_lpm", label: "Pump capacity (LPM)", type: "number" },
      { key: "pump_capacity_m3h", label: "Pump capacity (m³/h)", type: "number" },
      { key: "current_pump_issues", label: "Current pump issues", type: "multiselect", options: ["Leaks", "Cycling", "Low pressure", "High noise", "Vibration", "Burns out often", "Other"] },
      { key: "working_pressure_bar", label: "Working pressure (bar)", type: "number" },
      { key: "has_vsd", label: "Has Variable Speed Drive (VSD)?", type: "radio", options: yesNo },
      { key: "distribution_pipe_material", label: "Distribution pipe material", type: "multiselect", options: ["PE", "PVC", "Steel", "Copper", "PEX", "Galvanised", "Other"] },
      { key: "approximate_pipe_age_years", label: "Approximate pipe age (years)", type: "number" },
    ],
  },
  {
    key: "filtration",
    title: "8. Filtration & Treatment",
    fields: [
      { key: "filtration_present", label: "Filtration system present?", type: "radio", options: yesNo },
      { key: "primary_filtration_types", label: "Primary filtration type", type: "multiselect", options: ["Sediment", "Activated Carbon", "Ultrafiltration", "Reverse Osmosis", "UV", "Ozone", "Media (sand/glass)", "Iron removal", "Other"], show_if: { filtration_present: "Yes" } },
      { key: "system_brand_model", label: "System brand / model", type: "text", show_if: { filtration_present: "Yes" } },
      { key: "installed_by", label: "Installed by", type: "text", show_if: { filtration_present: "Yes" } },
      { key: "installed_capacity_lph", label: "Installed capacity (L/hr)", type: "number", show_if: { filtration_present: "Yes" } },
      { key: "installed_capacity_m3day", label: "Installed capacity (m³/day)", type: "number", show_if: { filtration_present: "Yes" } },
      { key: "point_of_use", label: "Point of use", type: "multiselect", options: ["Entire property", "Kitchen only", "Drinking water only", "Pool", "Irrigation", "Laundry", "Spa", "Other"] },
      { key: "remineralisation_present", label: "Remineralisation present?", type: "radio", options: yesNo },
      { key: "softener_installed", label: "Water softener installed?", type: "radio", options: yesNo },
      { key: "chlorination_in_use", label: "Chlorination in use?", type: "radio", options: yesNo },
      { key: "last_service_date", label: "Date of last service / maintenance", type: "date" },
      { key: "next_service_date", label: "Next scheduled service", type: "date" },
    ],
  },
  {
    key: "issues",
    title: "9. Known Issues & Pain Points",
    description: "NEW — what's frustrating the client *today*. The more brutal the truth, the better the solution we propose.",
    fields: [
      { key: "known_issues", label: "Known water/system issues (be specific)", type: "textarea", placeholder: "e.g. brown water mornings only, RO membrane failed twice in 6 months..." },
      { key: "guest_complaints", label: "Recurring guest/staff complaints", type: "multiselect", options: ["Taste", "Smell", "Cloudy water", "Skin irritation", "Hair issues", "Scale on taps", "Stains on linen", "Low pressure", "Hot water issues", "None"] },
      { key: "what_has_been_tried", label: "What's been tried that didn't work", type: "textarea" },
      { key: "biggest_frustration", label: "Single biggest frustration", type: "textarea" },
      { key: "ideal_outcome", label: "What does success look like for them?", type: "textarea" },
    ],
  },
  {
    key: "maintenance",
    title: "10. Maintenance & Service",
    fields: [
      { key: "maintenance_agreement", label: "Has a maintenance agreement?", type: "radio", options: yesNoPending, pre_fill: (l, c) => (c?.has_sla === true ? "Yes" : c?.has_sla === false ? "No" : "") },
      { key: "agreement_type", label: "Agreement type", type: "select", options: ["Annual", "Quarterly", "Monthly", "On-Demand"], show_if: { maintenance_agreement: ["Yes", "Pending"] } },
      { key: "installer_company", label: "Company that installed system", type: "text" },
      { key: "maintenance_company", label: "Company that maintains system (current provider)", type: "text", pre_fill: (l) => firstOf(l?.current_provider) },
      { key: "maintenance_contact_name", label: "Maintenance contact name", type: "text" },
      { key: "maintenance_contact_phone", label: "Maintenance contact phone", type: "text" },
      { key: "maintenance_sla", label: "SLA notes (e.g. 24h response, parts incl.)", type: "textarea" },
      { key: "spare_parts_on_site", label: "Spare parts on site?", type: "radio", options: yesNo },
      { key: "critical_spares_list", label: "Critical spares stored on site", type: "textarea" },
      { key: "warranty_details", label: "Warranty details", type: "textarea" },
      { key: "warranty_expiry", label: "Warranty expiry date", type: "date" },
    ],
  },
  {
    key: "water_quality",
    title: "11. Water Quality & Metrics",
    fields: [
      { key: "typical_quality_issues", label: "Typical water quality issues", type: "multiselect", options: ["Turbidity", "Bad taste", "Bad smell", "Iron", "Manganese", "Hardness", "High TDS", "Bacteria", "Nitrates", "Other"] },
      { key: "tds_mgl", label: "TDS (mg/L)", type: "number" },
      { key: "ph", label: "pH", type: "number" },
      { key: "hardness_caco3", label: "Hardness (mg/L CaCO₃)", type: "number" },
      { key: "turbidity_ntu", label: "Turbidity (NTU)", type: "number" },
      { key: "last_lab_test_date", label: "Last lab test date", type: "date" },
      { key: "last_lab_name", label: "Lab name", type: "text" },
      { key: "treatment_effectiveness", label: "Treatment effectiveness notes", type: "textarea" },
      { key: "post_treatment_problems", label: "Problems observed AFTER treatment", type: "textarea" },
    ],
  },
  {
    key: "compliance",
    title: "12. Health, Safety & Compliance",
    description: "NEW — saves us audit pain after install.",
    fields: [
      { key: "sans241_status", label: "SANS 241 compliance status", type: "select", options: ["Compliant", "Partial", "Unknown", "Non-compliant"] },
      { key: "last_sans_test_date", label: "Last SANS 241 test date", type: "date" },
      { key: "discharge_permits", label: "Effluent / discharge permits in place?", type: "radio", options: yesNo },
      { key: "regulatory_requirements", label: "Other regulatory requirements", type: "multiselect", options: ["Local Health Dept", "Discharge Permits", "SANBI / DEAT", "Tourism Grading Council", "ISO 22000", "HACCP", "Other"] },
      { key: "regulatory_notes", label: "Regulatory notes", type: "textarea" },
    ],
  },
  {
    key: "finance",
    title: "13. Contracts & Finance",
    fields: [
      { key: "contract_start", label: "Existing contract start date", type: "date" },
      { key: "contract_end", label: "Existing contract end date", type: "date" },
      { key: "contract_value", label: "Existing contract value (ZAR)", type: "number" },
      { key: "payment_terms", label: "Payment terms", type: "text", pre_fill: (l, c) => firstOf(c?.payment_terms_custom, c?.payment_terms) },
      { key: "decision_maker", label: "Decision maker", type: "text", pre_fill: (l, c) => firstOf(c?.contact_name, l?.own_contact_name) },
      { key: "procurement_process", label: "Procurement process (PO required, board sign-off, etc.)", type: "textarea" },
      { key: "budget_indication", label: "Budget indication (free text)", type: "text", pre_fill: (l, c) => firstOf(c?.price_per_night, l?.price_bracket) },
    ],
  },
  {
    key: "engagement",
    title: "14. Engagement & Sales Tracking",
    fields: [
      { key: "first_contact_date", label: "Date of 1st contact", type: "date", pre_fill: (l) => firstOf(l?.first_contact_date) },
      { key: "contacted_by", label: "Contacted by (rep name)", type: "text" },
      { key: "last_contact_date", label: "Last contact date", type: "date" },
      { key: "lead_source", label: "Lead source", type: "text", pre_fill: (l) => firstOf(l?.lead_source) },
      { key: "proposal_sent_date", label: "Proposal sent date", type: "date" },
      { key: "proposal_value", label: "Proposal value (ZAR)", type: "number", pre_fill: (l) => (l?.value && Number(l.value) > 0 ? Number(l.value) : "") },
      { key: "follow_up_date", label: "Next follow-up date", type: "date", pre_fill: (l) => firstOf(l?.next_followup_date) },
      { key: "next_action", label: "Next action", type: "textarea" },
      { key: "sales_notes", label: "Sales notes", type: "textarea", pre_fill: (l) => firstOf(l?.notes) },
    ],
  },
  {
    key: "signoff",
    title: "15. Rep Recommendation & Next Steps",
    description: "Internal notes from the rep — recommended solution, priority, next visit. Editable any time.",
    fields: [
      { key: "rep_recommended_solution", label: "Recommended solution (high level)", type: "textarea", pre_fill: (l) => Array.isArray(l?.service_interest) && l.service_interest.length ? l.service_interest.join(", ") : "" },
      { key: "rep_estimated_value_zar", label: "Estimated quote value (ZAR)", type: "number", pre_fill: (l) => (l?.value && Number(l.value) > 0 ? Number(l.value) : "") },
      { key: "rep_priority", label: "Lead priority", type: "select", options: ["Hot — close this month", "Warm — close this quarter", "Cool — long term", "Educational only"], pre_fill: (l) => {
        const r = Number(l?.lead_rating);
        if (!Number.isFinite(r) || r <= 0) return "";
        if (r >= 5) return "Hot — close this month";
        if (r >= 4) return "Warm — close this quarter";
        if (r >= 2) return "Cool — long term";
        return "Educational only";
      } },
      { key: "rep_next_visit_date", label: "Next site visit date", type: "date" },
      { key: "rep_signoff_notes", label: "Internal notes", type: "textarea" },
    ],
  },
];

export const ONBOARDING_FIELD_COUNT = ONBOARDING_SECTIONS.reduce(
  (n, s) => n + (s.fields?.length || 0) + (s.repeat_groups || []).reduce((m, g) => m + g.fields.length, 0),
  0,
);
